import React, { useState } from 'react';
import { motion } from 'framer-motion';

const MovieCard = React.memo(({ movie, onCardClick, onAddToLibrary, onRemoveFromLibrary, onManageCategories, userLibrary, index = 0 }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const isInLibrary = userLibrary.some(m => m.id === movie.id);
    const movieInLibrary = userLibrary.find(m => m.id === movie.id);
    const hasOnlyAllCategory = movieInLibrary && movieInLibrary.categories && movieInLibrary.categories.length === 1 && movieInLibrary.categories.includes('all');

    const handleAddToLibrary = (e) => {
        e.stopPropagation();
        onAddToLibrary(movie);
    };

    const handleRemoveFromLibrary = (e) => {
        e.stopPropagation();
        onRemoveFromLibrary(movie);
    };

    const handleManageCategories = (e) => {
        e.stopPropagation();
        if (onManageCategories && movieInLibrary) {
            onManageCategories(movieInLibrary);
        }
    };

    const handleViewDetails = (e) => {
        e.stopPropagation();
        onCardClick(movie);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: 0.4,
                delay: index * 0.05, // Stagger effect
                ease: [0.25, 0.1, 0.25, 1]
            }}
            whileHover={{ y: -8 }}
            className="relative w-64 h-96 md:w-80 md:h-[32rem] rounded-lg overflow-hidden cursor-pointer group transition-all duration-300 ease-in-out shadow-lg hover:shadow-2xl"
            onClick={handleViewDetails}
        >
            <img
                src={movie.poster}
                alt={movie.title}
                className={`w-full h-full object-cover transition-all duration-300 ease-in-out group-hover:scale-110 ${
                    imageLoaded ? 'image-loaded' : 'image-loading'
                }`}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://placehold.co/150x225/374151/D1D5DB?text=No+Poster`;
                    setImageLoaded(true);
                }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 transition-all duration-300 flex flex-col justify-between p-4">
                {/* Top section of overlay with Manage Categories button */}
                <div className="flex justify-end">
                    {hasOnlyAllCategory && movieInLibrary && (
                        <button
                            onClick={handleManageCategories}
                            className="px-4 py-2 text-sm font-bold text-white transition-colors duration-200 bg-[#e50914] hover:bg-[#b0060f]"
                        >
                            Manage Categories
                        </button>
                    )}
                </div>

                {/* Bottom section of overlay with info and buttons */}
                <div className="text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white break-words whitespace-normal">{movie.title} ({movie.year})</h3>
                    <div className="flex justify-center space-x-3 mt-4">
                        <motion.button
                            onClick={handleViewDetails}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-4 py-2 text-sm font-bold text-white transition-colors duration-200 bg-[#e50914] hover:bg-[#b0060f]"
                        >
                            Details
                        </motion.button>
                        {isInLibrary ? (
                            <motion.button
                                onClick={handleRemoveFromLibrary}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="px-4 py-2 text-sm font-bold text-white transition-colors duration-200 bg-red-600 hover:bg-red-700"
                            >
                                Remove
                            </motion.button>
                        ) : (
                            <motion.button
                                onClick={handleAddToLibrary}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="px-4 py-2 text-sm font-bold text-white transition-colors duration-200 bg-[#141414] hover:bg-[#333]"
                            >
                                Add to Library
                            </motion.button>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
});

MovieCard.displayName = 'MovieCard';

export default MovieCard;
